import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sameObjects } from '../../../modules/global';
import { Asterisk, ChevronDown } from 'lucide-react';

/**
 * options array must be in the form [ {value: ..., label: ...}, ... ]
 */
const SELECT_ITEM_HEIGHT = 30;

export default class Select extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    options: PropTypes.array.isRequired,
    noMargin: PropTypes.bool,
    name: PropTypes.string,
    label: PropTypes.string,
    className: PropTypes.string,
    defaultValue: PropTypes.any,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    errorMessage: PropTypes.any,
    onSelect: PropTypes.func,
    allowBlank: PropTypes.bool,
    color: PropTypes.oneOf(['dark', 'light']),
    data: PropTypes.string,
    disabled: PropTypes.bool,
    highlighted: PropTypes.bool,
    inputClassName: PropTypes.string,
    icon: PropTypes.object,
    required: PropTypes.bool,
    staticDisplayLabel: PropTypes.string,
    dropdownAlign: PropTypes.string,
  }

  static defaultProps = {
    id: '',
    type: 'text',
    defaultValue: '',
    tabIndex: '',
    name: '',
    options: [],
    color: 'dark',
    noMargin: false,
    allowBlank: true,
    onSelect: () => {},
    data: '',
    disabled: false,
    inputClassName: '',
    dropdownAlign: 'left',
  }

  constructor(props) {
    super(props);
    this.selectBox = React.createRef();

    this.state = {
      selectedOption: props.staticDisplayLabel
        ? {} // no selected value if we only show static label
        : props.options.find(option => option.value === (props.value || props.defaultValue)) || (props.allowBlank ? {} : props.options[0]),
      active: false,
      highlightedIndex: null,
      height: 0,
    };
  }

  componentDidUpdate(prevProps, _prevState) {
    const { staticDisplayLabel } = this.props;
    const prevOptions = this.optionCleaner(prevProps.options);
    const options = this.optionCleaner(this.props.options);

    if (!staticDisplayLabel && (!sameObjects(prevOptions, options) || prevProps.value != this.props.value)) {
      this.setState({
        selectedOption: (
          this.props.options.find(option => option.value === (this.props.value || this.props.defaultValue)) ||
          (this.props.allowBlank ? {} : this.props.options[0])
        )
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleSyntheticBlur);
  }

  optionCleaner = (options) => {
    return JSON.stringify(options, (key, value) => key == 'displayLabel' ? null : value);
  }

  get classNames() {
    const { className, noMargin, highlighted, icon } = this.props;
    return classNames('input-field', {
      'input-icon-container': icon,
      'highlighted': highlighted,
      'margin-bottom-0': noMargin,
      [className]: className,
    });
  }

  get inputClassNames() {
    const { inputClassName, color, icon } = this.props;
    return classNames({
      [inputClassName]: inputClassName,
      'is-light': color === 'light',
      'padding-left-35': icon,
    });
  }

  get dropDownHeight() {
    return Math.min(this.props.options.length * SELECT_ITEM_HEIGHT, 300);
  }

  get dropdownClassNames() {
    const { dropdownAlign } = this.props;
    return classNames('input-dropdown', {
      'absolute right-0 margin-left-auto': (dropdownAlign === 'right'),
    });
  }

  handleSyntheticBlur = (e) => {
    if (this.selectBox.current !== e.target) {
      this.closeDropdown();
      document.removeEventListener('click', this.handleSyntheticBlur);
    }
  }

  handleEnterOrSpace = () => {
    const { highlightedIndex } = this.state;
    const { options, data } = this.props;
    let option = options[highlightedIndex];
    option.data = data;
    this.setSelectedOption(option)
    this.closeDropdown();
  }

  handleKeyDown = (e) => {
    const { highlightedIndex, active } = this.state;
    const { options } = this.props;
    const { length } = options;

    switch (e.key) {
      case 'Tab':
        if (active) e.preventDefault();
        break;
      case ' ': // Space and Enter behave the same
        e.preventDefault();
        if (active && highlightedIndex !== null) {
          this.handleEnterOrSpace();
        } else if (!active) {
          this.openDropdown()
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (active && highlightedIndex !== null) {
          this.handleEnterOrSpace();
        }
        break;
      case 'Escape':
        this.closeDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (active) {
          this.setState({
            highlightedIndex: highlightedIndex === null ? 0 : (highlightedIndex + 1) % length
          }, () => this.scrollIntoView());
        } else {
          this.openDropdown();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (active) {
          this.setState({
            highlightedIndex: highlightedIndex ? (highlightedIndex - 1) % length : length - 1
          }, () => this.scrollIntoView());
        } else {
          this.openDropdown();
        }
        break;
      default:
        /* DO NOTHING */
        break;
    }
  }

  handleItemClick = (optionData) => {
    const { disabled } = this.props;
    if (disabled) return;
    this.setSelectedOption(optionData);
  }

  openDropdown = () => {
    const { disabled } = this.props;
    const { active, selectedOption } = this.state;

    // TODO: Review this code. Seems odd to set state this way.
    if (!active && !disabled) {
      const state = { active: true, height: this.dropDownHeight };
      document.addEventListener('click', this.handleSyntheticBlur);
      window.getSelection().removeAllRanges(); /* Prevents the input fields text to be highlighted */
      const highlightedIndex = this.props.options.findIndex(({ value }) => selectedOption.value === value);
      if (highlightedIndex > -1) state.highlightedIndex = highlightedIndex;
      this.setState(state, () => this.scrollIntoView());
    }
  }

  closeDropdown = () => {
    this.setState({ active: false, height: 0, highlightedIndex: null });
  }

  setSelectedOption = (selectedOption) => {
    const { disabled, onSelect, staticDisplayLabel } = this.props;
    if (!disabled && !selectedOption.disabled) {
      if (staticDisplayLabel) {
        onSelect(selectedOption); // Don't update internal selection
      } else {
        this.setState({
          selectedOption
        }, () => onSelect(selectedOption));
      }
    }
  }


  scrollIntoView = () => {
    const active = document.querySelector('.input-dropdown .is-active');

    if (active) {
      scrollIntoView(active, {
        behavior: 'smooth',
        scrollMode: 'if-needed',
      });
    }
  }

  renderList() {
    const { options, data, disabled, staticDisplayLabel } = this.props;
    const { height, highlightedIndex, selectedOption } = this.state;
    const selectedValue = staticDisplayLabel ? null : selectedOption.value;

    return (
      <ul className={this.dropdownClassNames} style={{height}}>
        {
          options.map((option, i) => (
            <li
              className={classNames({
                'is-disabled': disabled || option.disabled,
                'is-active': i === highlightedIndex,
                'is-selected': selectedValue === option.value,
              })}
              key={`select-${option.value}-${i}`}
              onClick={() => this.handleItemClick({...option, ...data })}
            >
              {option.displayLabel || option.label}
            </li>
          ))
        }
      </ul>
    )
  }

  renderIcon = () => {
    const { icon } = this.props;
    return icon && <FontAwesomeIcon className='input-icon left' icon={icon} />
  }

  render() {
    const { id, label, name, errorMessage, options, placeholder, disabled, staticDisplayLabel, required } = this.props;
    const { selectedOption } = this.state;

    return (
      <div className={this.classNames}>
        {
          label &&
          <label className='input-label'>
            {label}
            {
              required &&
              <span><Asterisk className='danger' size='14' /></span>
            }
          </label>
        }
        <div className='select-container input-dropdown-container '>
          <div className='vertical-align relative'>
            {this.renderIcon()}
            <input
              {...{ id, disabled }}
              readOnly
              type='text'
              placeholder={placeholder}
              value={staticDisplayLabel || selectedOption.label || ''}
              onClick={this.openDropdown}
              onKeyDown={this.handleKeyDown}
              className={this.inputClassNames}
              ref={this.selectBox}
            />
            <div className='absolute right-10 bottom-5'>
              <ChevronDown size={14} />
            </div>
          </div>
          {this.renderList()}
          <select
            name={name}
            value={staticDisplayLabel ? '' : selectedOption.value}
            readOnly
          >
            {
              options.map((option, i) => (
                <option
                  key={`${option.value}-${i}`}
                  value={option.value}
                  disabled={option.disabled}
                >{option.label}</option>
              ))
            }
          </select>
        </div>
        {
          errorMessage &&
          <div className='error-message'> {errorMessage} </div>
        }
      </div>
    );
  }
}